import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Clock } from 'lucide-react';
import { NotaRapida } from '@/components/timeline/NotaRapida';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { resolverPedidoAtivo } from '@/lib/pedidoVersionHelper';
import { ScrollArea } from '@/components/ui/scroll-area';
import { JustificativaFilaDialog } from '@/components/kanban/triagem/JustificativaFilaDialog';
import { useJustificativaFila } from '@/components/kanban/triagem/useJustificativaFila';

interface ManterComNotaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pedidoId: string;
  clienteId: string;
  onConfirm: () => void;
  onNotaSaved?: (textoNota: string) => void;
  /** Fila que exige justificativa para empurrar o item para o final. Sem ela o
   *  modal segue sendo a nota rápida de sempre, com "Manter sem Nota". */
  justificarFila?: { itemId: string; coluna: string; numeroPedido: string; razaoSocial: string; justificativaAtual?: string | null; diasParado?: number | null };
}

export function ManterComNotaModal({
  open,
  onOpenChange,
  pedidoId,
  clienteId,
  onConfirm,
  onNotaSaved,
  justificarFila,
}: ManterComNotaModalProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const { justificarAsync, isJustificando } = useJustificativaFila();

  const { data: timeline = [], isLoading } = useQuery({
    queryKey: ['timeline-pedido-modal', pedidoId],
    queryFn: async () => {
      const pedidoAtivoId = await resolverPedidoAtivo(pedidoId);
      const { data, error } = await supabase
        .from('vw_timeline_pedido')
        .select('*')
        .eq('pedido_id', pedidoAtivoId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data || [];
    },
    enabled: open && !!pedidoId,
  });

  const handleManterSemNota = async () => {
    setIsConfirming(true);
    try {
      onNotaSaved?.('');
      onConfirm();
      onOpenChange(false);
    } finally {
      setIsConfirming(false);
    }
  };

  if (justificarFila) {
    return <JustificativaFilaDialog open={open} onOpenChange={onOpenChange} coluna={justificarFila.coluna}
      numeroPedido={justificarFila.numeroPedido} razaoSocial={justificarFila.razaoSocial}
      justificativaAtual={justificarFila.justificativaAtual} diasParado={justificarFila.diasParado}
      isLoading={isJustificando}
      onConfirm={async (justificativa, codigos) => {
        await justificarAsync({ itemId: justificarFila.itemId, pedidoId, justificativa, motivoCodigo: codigos.motivo, acaoCodigo: codigos.acao });
        onNotaSaved?.(justificativa);
        onConfirm();
      }} />;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[60vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <DialogTitle className="text-base">Manter na Fila - Nota Rápida</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden space-y-3">
          {/* Nota Rápida - ao salvar, move para o final da fila automaticamente */}
          <NotaRapida clienteId={clienteId} pedidoId={pedidoId} onNotaText={(texto) => {
            onNotaSaved?.(texto);
          }} onSuccess={() => {
            onConfirm();
            onOpenChange(false);
          }} />

          {/* Mini Timeline - últimos 5 eventos */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Últimos eventos</span>
            <ScrollArea className="max-h-[180px]">
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : timeline.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">Nenhum evento registrado</p>
              ) : (
                <div className="space-y-1">
                  {timeline.map((item: any) => {
                    const date = item.created_at
                      ? format(new Date(item.created_at), "dd/MM HH:mm", { locale: ptBR })
                      : '';
                    return (
                      <div
                        key={item.id || item.created_at}
                        className="flex items-start gap-2 text-xs py-1 px-2 rounded bg-muted/40"
                      >
                        <span className="shrink-0 text-muted-foreground">{date}</span>
                        <span className="flex-1 truncate" title={item.motivo || item.corpo || ''}>
                          <span className="font-medium">{item.tipo || 'Nota'}</span>
                          {' - '}
                          {item.origem === 'movimento' ? (item.motivo || 'Movimento') : (item.corpo || item.motivo || 'Evento')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        {/* Botão Manter sem Nota */}
        <div className="flex justify-end pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={handleManterSemNota}
            disabled={isConfirming}
          >
            {isConfirming ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Clock className="h-3.5 w-3.5 mr-1" />}
            Manter sem Nota
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
